/**
 * Oil Conversion Utilities
 *
 * Oil is purchased in bulk and priced per TONNE (1,000 kg) in USD.
 * Recipes and batch consumption specify oil usage in KG; the stock ledger
 * (`oil_types.current_stock_liters`) and `average_cost_clp` are per-LITER.
 * These utilities handle conversions between tonnes, kg, liters, and currencies.
 *
 * Key concept: Oil is LESS dense than water (~0.92 kg/L for olive oil)
 * So: 1 kg of oil = MORE than 1 liter (1 kg ÷ 0.92 = 1.087 liters)
 */

/**
 * Convert kilograms to liters
 * Oil is less dense than water, so 1 kg = more than 1 liter
 */
export const kgToLiters = (kg: number, density: number): number => {
  if (density <= 0) throw new Error("Density must be positive");
  return kg / density;
};

/**
 * Convert liters to kilograms
 * Oil is less dense than water, so 1 liter = less than 1 kg
 */
export const litersToKg = (liters: number, density: number): number => {
  return liters * density;
};

/**
 * Calculate how many liters are in 1 tonne (1,000 kg) of oil
 * Example: Olive oil (0.92 kg/L) → 1000 / 0.92 = ~1,087 liters per tonne
 */
export const litersPerTonne = (density: number): number => {
  if (density <= 0) throw new Error("Density must be positive");
  return 1000 / density;
};

/**
 * Calculate how many tonnes are equivalent to a given volume of liters
 */
export const litersToTonnes = (liters: number, density: number): number => {
  const kg = litersToKg(liters, density);
  return kg / 1000;
};

/**
 * Convert tonnes to liters
 */
export const tonnesToLiters = (tonnes: number, density: number): number => {
  const kg = tonnes * 1000;
  return kgToLiters(kg, density);
};

/**
 * Calculate cost per liter in USD from price per tonne
 * Example: $5,500 USD/tonne ÷ 1,087 liters/tonne = $5.06 USD/liter
 */
export const costPerLiterUsd = (
  priceUsdPerTonne: number,
  density: number
): number => {
  return priceUsdPerTonne / litersPerTonne(density);
};

/**
 * Calculate cost per liter in CLP from USD price per tonne
 */
export const costPerLiterClp = (
  priceUsdPerTonne: number,
  density: number,
  exchangeRate: number
): number => {
  const costUsd = costPerLiterUsd(priceUsdPerTonne, density);
  return costUsd * exchangeRate;
};

/**
 * Convert an oil's per-LITER cost (average_cost_clp) to a per-KG cost.
 * Oil cost is stored per liter; 1 liter weighs `density` kg, so $/kg = $/L ÷ density.
 */
export const costPerKgClp = (
  costPerLiterClp: number,
  density: number
): number => {
  if (density <= 0) throw new Error("Density must be positive");
  return costPerLiterClp / density;
};

/**
 * Convert USD amount to CLP
 */
export const usdToClp = (amountUsd: number, exchangeRate: number): number => {
  return amountUsd * exchangeRate;
};

/**
 * Convert CLP amount to USD
 */
export const clpToUsd = (amountClp: number, exchangeRate: number): number => {
  if (exchangeRate <= 0) throw new Error("Exchange rate must be positive");
  return amountClp / exchangeRate;
};

/**
 * Calculate total oil cost for a given volume
 */
export const calculateOilCost = (
  liters: number,
  priceUsdPerTonne: number,
  density: number,
  exchangeRate: number
): { costUsd: number; costClp: number } => {
  const costPerLiter = costPerLiterUsd(priceUsdPerTonne, density);
  const costUsd = liters * costPerLiter;
  const costClp = costUsd * exchangeRate;

  return {
    costUsd: Math.round(costUsd * 100) / 100,
    costClp: Math.round(costClp),
  };
};

/**
 * Format a number as currency (USD)
 */
export const formatUsd = (amount: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

/**
 * Format liters with unit
 */
export const formatLiters = (liters: number): string => {
  return `${new Intl.NumberFormat("es-CL", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(liters)} L`;
};

/**
 * Format quantity with appropriate decimal places
 */
export const formatQuantity = (quantity: number, decimals = 2): string => {
  return new Intl.NumberFormat("es-CL", {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  }).format(quantity);
};
